import { Theme, Box, Stack, RadioGroup, useMediaQuery, Typography } from '@mui/material';
import React, { FC, useState, useEffect, useCallback } from 'react';
import Divider from '@mui/material/Divider/';
import LoadsService from '../../providers/Loads/Loads.service';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import StringUtils from '../../utils/stringUtils';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import TwDialog from '../TwDialog/TwDialog';
import TwShareHistory from '../TwLoadShareHistory/TwLoadShareHistory';
import './TwNotifyDriver.scss';
import NotifySegmentsBody from './TwNotifySegmentsBody';
import { useGetDriversQuery } from '../../redux/api/driver/driverApi';
import TwAutocomplete, { TwAutocompleteOption } from '../TwAutocomplete/TwAutocomplete';
import TwTooltip from '../TwTooltip/TwTooltip';
import { Driver } from '../../redux/api/driver/driver.model';
import TwRadioButton from '../TwRadioButtons/TwRadioButtons';
import { ShareType } from '../../redux/api/load/load.model';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useAuth from '../../providers/Auth/Auth.provider';
import { Trip, TripDriver, TripSharePreviewParams } from '../../redux/api/trips/trips.model';
import { Order, OrderSharePreviewParams } from '../../redux/api/orders/orders.model';

import { useGetDriverLinksForTripQuery, useShareTripMutation } from '../../redux/api/trips/tripApi';
import { useAppSelector } from '../../redux/hooks';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { ReactComponent as LinkIcon } from '../../assets/icons/link.svg';
import { useGetDriverLinksForOrderQuery } from '../../redux/api/orders/ordersApi';
export interface INotifyDriverWithFullSegment {
  driverId: string;
  driverIndex: number;
  fullSegment: Trip;
  isForExistingLoadUpdate?: boolean;
  deleted_trip_index?: number;
  removed?: boolean;
}

interface INotifyDriverProps {
  driversToNotify: INotifyDriverWithFullSegment[];
  unassignedDriversToNotify: INotifyDriverWithFullSegment[];
  orderId?: string;
  tripId?: string;
  open: boolean;
  onClose: () => void;
  isNormalShare?: boolean; // Only shares load on demand
  isSegmentShare?: boolean; // Only shares load segment with its driver
  isSplitLoad: boolean;
  isForNewLoad?: boolean;
}

const TwNotifyDriver: FC<INotifyDriverProps> = ({
  driversToNotify,
  unassignedDriversToNotify,
  orderId,
  tripId,
  open,
  onClose,
  isNormalShare,
  isSplitLoad,
  isForNewLoad
}) => {
  const {
    data: { userData }
  } = useAuth();
  const filterParams = useAppSelector((state) => state.activeTrips);
  const { addNotification } = useNotifications();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const [copied, setCopied] = useState<boolean>(false);
  const [assignedPayload, setAssignedPayload] = useState<OrderSharePreviewParams[]>([]);
  const [unassignedPayload, setUnassignedPayload] = useState<OrderSharePreviewParams[]>([]);

  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { data: drivers = { content: [], total: 0 } } = useGetDriversQuery({});

  const [shareTripPreview] = useShareTripMutation();
  /** For single share */
  const [load, setLoad] = useState<Order | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<Driver | undefined>(undefined);
  const [selectedDriverLink, setSelectedDriverLink] = useState<string | undefined>(undefined);
  const [radio, setRadio] = useState('');

  const [autocompleteDrivers, setAutocompleteDrivers] = useState<TwAutocompleteOption[]>([]);
  const [selectedDriverOption, setSelectedDriverOption] = useState<
    TwAutocompleteOption | undefined | null
  >(null);

  const [isSending, setIsSending] = useState<boolean>(false);
  const selectedTripId = useAppSelector((state) => state.trip.tripId);
  const { data: driverLinks } = useGetDriverLinksForTripQuery(
    selectedTripId || tripId || skipToken,
    {
      skip: !open || (!selectedTripId && !tripId)
    }
  );

  const { data: orderDriverLinks } = useGetDriverLinksForOrderQuery(orderId || skipToken, {
    skip: !open || !orderId
  });

  const messageTypeForDriver = 'SHARE';
  const shareEntityType = (selectedTripId || tripId) && enableLtl ? 'trip' : 'order';
  const shareReadableId =
    (selectedTripId || tripId) && enableLtl
      ? driverLinks?.trip?.readable_id
      : load?.readable_id || '';

  // map of trips where value is map of drivers and value is driverLink
  const [tripDriversLinkMaps, setTripDriversLinkMaps] =
    useState<Map<string, Map<string, string>>>();

  // useEffect on orderDriverLinks if changes set load to order object
  const initDriverLinks = (): void => {
    if (!driverLinks || !driverLinks.trip) return;
    const driverLinksMap = new Map<string, string>();
    driverLinks.drivers.forEach((driver) => {
      driverLinksMap.set(driver.driver.id, driver.link);
    });
    const mapOfDriversAndLinks = new Map<string, Map<string, string>>();
    mapOfDriversAndLinks.set(driverLinks.trip.id, driverLinksMap);
    setTripDriversLinkMaps(mapOfDriversAndLinks);
  };

  const initOrderDriverLinks = (): void => {
    // return if data is null
    if (!orderDriverLinks) return;
    if (orderDriverLinks && orderDriverLinks.trips) {
      setLoad(orderDriverLinks.order);
      const tripDriversLinkMap = new Map<string, Map<string, string>>();

      orderDriverLinks.trips.forEach((trip) => {
        const driversLinkMap = new Map<string, string>();
        trip.drivers.forEach((driver) => {
          driversLinkMap.set(driver.driver.id, driver.link);
        });
        tripDriversLinkMap.set(trip.trip.id, driversLinkMap);
      });
      setTripDriversLinkMaps(tripDriversLinkMap);
    }
  };

  /** Generates data to send update to assigned drivers */
  const initAssignedDriversPayload = useCallback(() => {
    if (open) {
      const listOfAssignedDrivers = initDriversPayLoadListForMessageType(
        messageTypeForDriver,
        driversToNotify
      );
      setAssignedPayload(listOfAssignedDrivers);
      initDriverLinks(); // Call initDriverLinks after setting assigned payload
      initOrderDriverLinks(); // Call initOrderDriverLinks after setting assigned payload
    }
  }, [open, driversToNotify, orderDriverLinks, driverLinks]);

  const initDriversPayLoadListForMessageType = (
    messageType: 'UPDATE' | 'UNASSIGNED' | 'SHARE',
    driversList: INotifyDriverWithFullSegment[]
  ): OrderSharePreviewParams[] => {
    const allInfo = driversList?.map((notifyTo: INotifyDriverWithFullSegment, i: number) => {
      const find = drivers?.content.find((driver) => driver.id === notifyTo.driverId);
      return {
        driver_name: find?.name,
        driver_id: notifyTo.driverId,
        trip_id: notifyTo.fullSegment.id,
        via_sms: !!find?.phone,
        via_email: !!find?.email_address && !find?.phone,
        message_type: notifyTo.isForExistingLoadUpdate ? 'UPDATE' : messageType,
        deleted_trip_index: notifyTo.deleted_trip_index
      };
    });

    return allInfo;
  };

  /** Generates data to send update to unassigned drivers */
  const initUnassignedDriversPayload = useCallback(() => {
    if (open) {
      const listOfUnassignedDrivers = initDriversPayLoadListForMessageType(
        'UNASSIGNED',
        unassignedDriversToNotify
      );

      setUnassignedPayload(listOfUnassignedDrivers);
    }
  }, [open, unassignedDriversToNotify]);

  const handleClose = (): void => {
    setAssignedPayload([]);
    setUnassignedPayload([]);
    setSelectedDriver(undefined);
    setSelectedDriverOption(null);
    setIsSending(false);
    setLoad(null);
    setSelectedDriverLink(undefined);
    onClose && onClose();
  };

  useEffect(() => {
    // Init drivers
    const allDrivers = [...(drivers?.content || [])];
    const newDrivers: TwAutocompleteOption[] = allDrivers.map((driver) => ({
      id: driver.id,
      label: `${driver.first_name} ${driver.last_name}`,
      value: driver.id
    }));

    setAutocompleteDrivers(newDrivers);
  }, [JSON.stringify(drivers.content)]);

  const getDriversList = (): { driver: TripDriver; link: string | null }[] => {
    let drivers: { driver: TripDriver; link: string | null }[] = [];

    if (driverLinks?.trip && driverLinks?.drivers) {
      drivers = driverLinks?.drivers || [];
      return drivers.map((driver) => ({ driver: driver.driver, link: driver.link }));
    }
    if (orderDriverLinks?.order && orderDriverLinks?.trips) {
      if (tripId) {
        const trip = orderDriverLinks?.trips?.find((trip) => trip.trip.id === tripId);
        drivers = trip?.drivers || [];
        return drivers.map((driver) => ({ driver: driver.driver, link: driver.link }));
      } else {
        orderDriverLinks?.trips?.forEach((segment) => {
          segment.drivers?.forEach((driver) =>
            drivers.push({ driver: driver.driver, link: driver.link })
          );
        });
      }
    }

    return drivers;
  };

  useEffect(() => {
    const drivers: { driver: TripDriver; link: string | null }[] = getDriversList();
    if (
      !!selectedDriver?.id &&
      drivers &&
      drivers.length > 0 &&
      selectedDriver.id === drivers[0].driver.id
    ) {
      if (selectedDriver?.phone) {
        setRadio('phone');
      } else {
        selectedDriver?.email_address && setRadio('email');
      }
    }
  }, [selectedDriver]);

  useEffect(() => {
    // Load driver
    let savedDriver = null;

    const drivers: { driver: TripDriver; link: string | null }[] = getDriversList();

    if (drivers.length > 0) {
      savedDriver = {
        id: drivers[0].driver.id,
        label: `${drivers[0].driver.first_name} ${drivers[0].driver.last_name}`,
        value: drivers[0].driver
      };

      if (savedDriver?.value.phone) {
        setRadio('phone');
      } else if (savedDriver?.value.email_address) {
        setRadio('email');
      } else {
        setRadio('');
      }

      setSelectedDriverOption(savedDriver);
      setSelectedDriver(drivers[0].driver as unknown as Driver);
      if (drivers[0].link) {
        setSelectedDriverLink(drivers[0].link);
      }
    }
  }, [load, driverLinks?.trip?.id, orderDriverLinks?.order?.id]);

  useEffect(() => {
    open && driversToNotify && initAssignedDriversPayload();
  }, [driversToNotify, open, orderDriverLinks, driverLinks]);

  useEffect(() => {
    open && unassignedDriversToNotify && initUnassignedDriversPayload();
  }, [unassignedDriversToNotify, open]);

  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopy = async (link: string): Promise<void> => {
    if (!link) {
      return;
    }
    await StringUtils.copyToClipboard(link);
    setLinkCopied(true);

    // Reset the "Link copied!" message after 2 seconds
    setTimeout(() => {
      setLinkCopied(false);
    }, 2000);
  };

  const sharePreview = async (): Promise<void> => {
    const tripsForlink: {
      trip: Trip;
      drivers: {
        driver: TripDriver;
        link: string;
      }[];
    }[] = orderDriverLinks?.trips
      ? orderDriverLinks?.trips
      : driverLinks
      ? [{ trip: driverLinks?.trip, drivers: driverLinks?.drivers }]
      : [];

    const tripIdToShare =
      selectedTripId ||
      tripId ||
      tripsForlink?.find((trip) =>
        drivers?.content?.some((driver) => driver.id === selectedDriver?.id)
      )?.trip?.id || // try to find correct trip id for driver
      tripsForlink[0]?.trip?.id;
    if (!tripIdToShare) return;
    const instructions: TripSharePreviewParams = {
      driver_id: selectedDriver?.id ? selectedDriver.id : '',
      driver_name: selectedDriver?.name ? selectedDriver.name : '',
      via_email: radio === 'email',
      via_sms: radio === 'phone',
      message_type: messageTypeForDriver,
      order_id: orderId || undefined
    };

    try {
      const response = await shareTripPreview({
        id: tripIdToShare,
        instructions,
        searchTripParams: {
          filters: {
            ...filterParams?.filters
          }
        }
      }).unwrap();

      if (response === 200) {
        addNotification({
          message: `${
            shareEntityType == 'trip' ? 'Trip' : 'Order'
          } #${shareReadableId} has been sent to ${selectedDriver?.name}`,
          variant: 'success'
        });
      }
    } catch (e: any) {
      if (e.status === 409) {
        const firstSentenceWithDriverName = selectedDriver?.name
          ? `Unable to send ${shareEntityType} update to driver(s) ${selectedDriver?.name}. `
          : '';

        addNotification({
          message: `${firstSentenceWithDriverName}
            ${
              e.data?.message ||
              `Unable to share ${shareEntityType} link. Please check recipient [phone number/email] and try again`
            }`,
          variant: 'error'
        });
      } else {
        console.error('Error sending driver link', e);
        addNotification({
          message: `Unable to share ${shareEntityType} link. Please check recipient [phone number/email] and try again`,
          variant: 'error'
        });
      }

      logRocketTrack('Dispatch', {
        action: 'Send to driver / share order',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
    } finally {
      handleClose();
    }
  };

  const sendUpdate = async (): Promise<void> => {
    setIsSending(true);
    const combinedPayload = [...(assignedPayload || []), ...(unassignedPayload || [])];

    try {
      if (combinedPayload && combinedPayload?.length > 0) {
        let cleanPayload: OrderSharePreviewParams[] = [];
        cleanPayload = combinedPayload.map((r) => ({
          driver_id: r.driver_id,
          driver_name: r.driver_name,
          via_email: r.via_email,
          via_sms: r.via_sms,
          trip_id: isSplitLoad || !isNormalShare ? r.trip_id : load?.trips[0]?.id || tripId,
          message_type: r.message_type,
          deleted_trip_index: r.deleted_trip_index
        }));

        cleanPayload = cleanPayload.filter((obj) => obj.via_email || obj.via_sms);

        const successfulRows: OrderSharePreviewParams[] = [];
        const failedRows: {
          share: OrderSharePreviewParams;
          error: { status?: number; data?: { message?: string } };
        }[] = [];

        for (let i = 0; i < cleanPayload.length; i++) {
          const { trip_id: tripId, ...instructions } = cleanPayload[i];
          if (!tripId) continue;
          await shareTripPreview({
            id: tripId,
            instructions: { ...instructions, order_id: orderId || undefined },
            searchTripParams: {
              filters: {
                ...filterParams?.filters
              }
            }
          })
            .unwrap()
            .then((response) => {
              if (response === 200) {
                successfulRows.push(cleanPayload[i]);
              }
            })
            .catch((error) => {
              console.error('Error sending sms', error);

              failedRows.push({ share: cleanPayload[i], error: error });
            });
        }

        if (failedRows.length > 0) {
          // make list of driver names
          const driverNames = failedRows.map((row) => row.share.driver_name).join(', ');

          // find a row if it has error status 409
          const conflictRow = failedRows.find((row) => row.error.status === 409);

          if (conflictRow) {
            addNotification({
              message: `Unable to send order update to driver(s) ${driverNames}. ${conflictRow.error.data?.message}`,
              variant: 'error'
            });
          } else {
            addNotification({
              message: `Unable to send order update to driver(s) ${driverNames} . Please check recipient [phone number/email] and try again`,
              variant: 'error'
            });
          }
          logRocketTrack('Dispatch', {
            action: 'Dispatch to driver',
            severity: 'low',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
        } else if (successfulRows.length > 0) {
          const allShareMessages = successfulRows.every((row) => row.message_type === 'SHARE');
          const allDriversHaveNames = successfulRows.every((row) => row.driver_name);
          if (allShareMessages && allDriversHaveNames) {
            const driverNames = successfulRows.map((row) => row.driver_name).join(', ');
            addNotification({
              message: `${
                shareEntityType == 'trip' ? 'Trip' : 'Order'
              } ${shareReadableId} has been sent to ${driverNames}`,
              variant: 'success'
            });
          } else if (allDriversHaveNames) {
            const driverNames = successfulRows.map((row) => row.driver_name).join(', ');
            addNotification({
              message: `${
                shareEntityType == 'trip' ? 'Trip' : 'Order'
              } update has been sent to ${driverNames}`,
              variant: 'success'
            });
          } else {
            addNotification({
              message: `${shareEntityType == 'trip' ? 'Trip' : 'Order'} update has been sent`,
              variant: 'success'
            });
          }
        }
      }
    } catch (e) {
      console.error(e);
      logRocketTrack('Dispatch', {
        action: 'Dispatch to driver',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: `Unable to send order update to driver. Please check recipient [phone number/email] and try again`,
        variant: 'error'
      });
      setIsSending(false);
    } finally {
      handleClose();
    }
  };

  const updatePayloadForUnassigned = (
    id: string,
    radio: string,
    segmentId: string,
    removed: boolean,
    oldDriverId?: string,
    driverName?: string,
    deletedSegmentIndex?: number
  ): void => {
    if (unassignedPayload && unassignedDriversToNotify.length > 0) {
      const newList = updateLoadSharePreviewList(
        id,
        radio,
        segmentId,
        removed,
        unassignedPayload,
        'UNASSIGNED',
        oldDriverId,
        driverName,
        deletedSegmentIndex
      );
      setUnassignedPayload(newList);
    }
  };

  const updatePayloadForAssigned = (
    id: string,
    radio: string,
    segmentId: string,
    removed: boolean,
    oldDriverId?: string,
    driverName?: string
  ): void => {
    if (assignedPayload?.length > 0 && driversToNotify.length > 0) {
      const newList = updateLoadSharePreviewList(
        id,
        radio,
        segmentId,
        removed,
        assignedPayload,
        messageTypeForDriver,
        oldDriverId,
        driverName
      );
      setAssignedPayload(newList);
    }
  };

  const updateLoadSharePreviewList = (
    id: string,
    radio: string,
    segmentId: string,
    removed: boolean,
    list: OrderSharePreviewParams[],
    messageType: 'UPDATE' | 'UNASSIGNED' | 'SHARE',
    oldDriverId?: string,
    driverName?: string,
    deletedSegmentIndex?: number
  ): OrderSharePreviewParams[] => {
    let finalList: OrderSharePreviewParams[] | undefined = list;

    if (removed) {
      // Find the removed driver and filter it from the list
      finalList = finalList?.filter((segmentDriverInfo) => {
        return segmentDriverInfo.driver_id !== id;
      });
    }

    if (oldDriverId) {
      // Find the previously selected driver and filter it from the list
      finalList = finalList?.filter((segmentDriverInfo) => {
        return segmentDriverInfo.driver_id !== oldDriverId;
      });
    }

    // Check if selected driver already exists in the list
    const findDriver = finalList?.find((segmentDriverInfo) => {
      return segmentDriverInfo.driver_id === id && segmentDriverInfo.trip_id === segmentId;
    });

    // If driver is not found and it isn't being removed, add it to the list
    if (!findDriver && !removed) {
      finalList = [
        ...finalList,
        {
          driver_id: id,
          driver_name: driverName,
          trip_id: segmentId,
          via_sms: radio === 'phone',
          via_email: radio === 'email',
          message_type: messageType,
          deleted_trip_index: deletedSegmentIndex
        }
      ];
    } else {
      finalList = finalList?.map((segmentDriverInfo) => {
        if (segmentDriverInfo.driver_id === id && segmentDriverInfo.trip_id === segmentId) {
          return {
            ...segmentDriverInfo,
            trip_id: segmentId,
            driver_name: driverName,
            via_sms: radio === 'phone',
            via_email: radio === 'email',
            deleted_trip_index: deletedSegmentIndex
          };
        } else return { ...segmentDriverInfo };
      });
    }

    return finalList;
  };

  const getAssignedDriverLabel = (driver: INotifyDriverWithFullSegment): string => {
    if (isSplitLoad) {
      const filteredDrivers = driversToNotify.filter((dr) => dr.driverId !== driver.driverId);

      const isTeamDriver = filteredDrivers.find(
        (dr) => dr.fullSegment.id === driver.fullSegment.id
      );

      if (isTeamDriver) {
        return `Trip ${driver.fullSegment.sort_order + 1} - Driver ${driver.driverIndex + 1}:`;
      } else {
        return `Trip ${driver.fullSegment.order_readable_id}-${
          driver.fullSegment.sort_order + 1
        } dispatch:`;
      }
    } else {
      if (driversToNotify.length > 1) {
        return `Driver ${driver.driverIndex + 1} dispatch:`;
      } else {
        return 'Send to:';
      }
    }
  };

  const getUnassignedDriverLabel = (driver: INotifyDriverWithFullSegment): string => {
    if (isSplitLoad) {
      return `Trip ${driver.fullSegment.sort_order + 1} unassigned driver:`;
    } else {
      if (unassignedDriversToNotify.length > 1) {
        return `Driver ${driver.driverIndex + 1} unassigned:`;
      } else {
        return 'Unassigned driver:';
      }
    }
  };

  const shareModalTitle = `Share ${shareEntityType} ${
    (selectedTripId || tripId) && enableLtl
      ? driverLinks?.trip?.readable_id
        ? `#${driverLinks?.trip?.readable_id}`
        : ''
      : load?.readable_id
      ? `#${load?.readable_id}`
      : ''
  }`;

  const shareHistoryEntityId = enableLtl ? selectedTripId || tripId : orderId;

  return (
    <>
      <TwDialog
        title={shareModalTitle}
        size="sm"
        open={open}
        fullScreen={matchesMobileOrTablet}
        onClickClose={handleClose}
        sx={{ '& .MuiDialogContent-root': { padding: '0 !important' } }}
      >
        {!isNormalShare && (
          <>
            {driversToNotify
              .filter((driver) => {
                const payloadItem = assignedPayload.find(
                  (payload) =>
                    payload.driver_id === driver.driverId &&
                    payload.trip_id === driver.fullSegment.id
                );
                return !driver.removed && !!payloadItem;
              })
              .map((driver: INotifyDriverWithFullSegment, indx: number) => (
                <Box
                  key={`segment-${driver.fullSegment.id}-${indx}`}
                  className="notify-driver__container-body"
                  sx={{ borderTop: indx === 0 ? '1px solid transparent !important' : 'inherit' }}
                >
                  <p className="notify-driver__text-drivers">{getAssignedDriverLabel(driver)}</p>
                  <NotifySegmentsBody
                    driver={driver}
                    driverLink={
                      tripDriversLinkMaps?.get(driver.fullSegment.id)?.get(driver.driverId) || null
                    }
                    driversList={drivers?.content || []}
                    handleUpdateDriver={updatePayloadForAssigned}
                    allowRemove={
                      driversToNotify.filter((d) => {
                        const payloadItem = assignedPayload.find(
                          (payload) =>
                            payload.driver_id === d.driverId && payload.trip_id === d.fullSegment.id
                        );
                        return !d.removed && !!payloadItem;
                      }).length > 1 ||
                      (driversToNotify.filter((d) => {
                        const payloadItem = assignedPayload.find(
                          (payload) =>
                            payload.driver_id === d.driverId && payload.trip_id === d.fullSegment.id
                        );
                        return !d.removed && !!payloadItem;
                      }).length === 1 &&
                        unassignedDriversToNotify.filter((d) => {
                          const payloadItem = unassignedPayload.find(
                            (payload) =>
                              payload.driver_id === d.driverId &&
                              payload.trip_id === d.fullSegment.id
                          );
                          return !d.removed && !!payloadItem;
                        }).length > 0)
                    }
                  />
                </Box>
              ))}

            {unassignedDriversToNotify
              .filter((driver) => {
                const payloadItem = unassignedPayload.find(
                  (payload) =>
                    payload.driver_id === driver.driverId &&
                    payload.trip_id === driver.fullSegment.id
                );
                return !driver.removed && !!payloadItem;
              })
              .map((driver: INotifyDriverWithFullSegment, indx: number) => (
                <Box
                  key={`segment-${driver.fullSegment.id}-${indx}`}
                  className="notify-driver__container-body"
                  sx={{ borderTop: indx === 0 ? '1px solid transparent !important' : 'inherit' }}
                >
                  <p className="notify-driver__text-drivers">{getUnassignedDriverLabel(driver)}</p>
                  <NotifySegmentsBody
                    driver={driver}
                    driverLink={null /* No link for unassigned drivers*/}
                    driversList={drivers?.content || []}
                    handleUpdateDriver={updatePayloadForUnassigned}
                    allowRemove={
                      unassignedDriversToNotify.filter((d) => {
                        const payloadItem = unassignedPayload.find(
                          (payload) =>
                            payload.driver_id === d.driverId && payload.trip_id === d.fullSegment.id
                        );
                        return !d.removed && !!payloadItem;
                      }).length > 1 ||
                      (unassignedDriversToNotify.filter((d) => {
                        const payloadItem = unassignedPayload.find(
                          (payload) =>
                            payload.driver_id === d.driverId && payload.trip_id === d.fullSegment.id
                        );
                        return !d.removed && !!payloadItem;
                      }).length === 1 &&
                        driversToNotify.filter((d) => {
                          const payloadItem = assignedPayload.find(
                            (payload) =>
                              payload.driver_id === d.driverId &&
                              payload.trip_id === d.fullSegment.id
                          );
                          return !d.removed && !!payloadItem;
                        }).length > 0)
                    }
                  />
                </Box>
              ))}

            <Box className="notify-driver__history">
              <TwShareHistory
                id={shareHistoryEntityId || ''}
                shareType={ShareType.load_driver_link}
              />
            </Box>
          </>
        )}

        {isNormalShare && (
          <>
            <Box className="notify-driver__container">
              <Box className="notify-driver__driver-container tid-notify-driver__driver-container">
                <span className="notify-driver__label">Send to</span>
                <div className="tid-notify-driver__driver">
                  <TwAutocomplete
                    id="driver"
                    name="driver"
                    value={selectedDriverOption || null}
                    optionList={autocompleteDrivers}
                    disabled={true}
                  />
                </div>

                <div className="notify-driver__send-options">
                  <Stack
                    spacing={3}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    className="notify-driver__send-options"
                  >
                    <RadioGroup
                      name="radiogroup-drivers"
                      sx={{ flexDirection: 'row' }}
                      value={radio}
                      onChange={(e): void => {
                        setRadio(e.target.value);
                      }}
                    >
                      {!selectedDriver?.phone ? (
                        <TwTooltip
                          title="Selected driver must have a valid phone number to share via SMS"
                          placement="top-end"
                        >
                          <Box>
                            <TwRadioButton
                              value="phone"
                              onInputChange={(): null => null}
                              name="radio"
                              label="Send as text message"
                              disabled={true}
                              classNames="notify-driver__send-label"
                            />
                            <span aria-label="tooltip-sms"></span>
                          </Box>
                        </TwTooltip>
                      ) : (
                        <TwRadioButton
                          name="radio"
                          label="Send as text message"
                          value="phone"
                          onInputChange={(): null => null}
                          classNames="notify-driver__send-label"
                          disabled={!selectedDriver}
                        />
                      )}

                      {!selectedDriver?.email_address ? (
                        <TwTooltip
                          title="Selected driver must have a valid email to share via email"
                          placement="top-end"
                        >
                          <Box>
                            <TwRadioButton
                              value="email"
                              onInputChange={(): null => null}
                              name="radio"
                              disabled={true}
                              label="Send via email"
                              classNames="notify-driver__send-label"
                            />
                            <span aria-label="tooltip-email"></span>
                          </Box>
                        </TwTooltip>
                      ) : (
                        <TwRadioButton
                          name="radio"
                          label="Send via email"
                          value="email"
                          onInputChange={(): null => null}
                          classNames="notify-driver__send-label"
                          disabled={!selectedDriver}
                        />
                      )}
                    </RadioGroup>
                    {selectedDriverLink && (
                      <span
                        className="notify-driver__send-label"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <LinkIcon />
                        <Typography
                          component={'span'}
                          sx={{ color: '#3A4DE9', fontSize: '14px', cursor: 'pointer' }}
                          onClick={(): Promise<void> => handleCopy(selectedDriverLink || '')}
                        >
                          {linkCopied ? 'Link copied!' : 'Copy dispatch link'}
                        </Typography>
                      </span>
                    )}
                  </Stack>
                </div>
                <Box className="notify-driver__history-normal-share">
                  <TwShareHistory
                    id={shareHistoryEntityId || ''}
                    shareType={ShareType.load_driver_link}
                  />
                </Box>
                <div className="notify-driver__send-options">
                  <Stack
                    spacing={1}
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    width="100%"
                  >
                    <TwButton
                      label="Skip"
                      action={(): void => handleClose()}
                      type={TwButtonType.secondary}
                    />
                    <TwButton
                      label="Share"
                      action={(): Promise<void> => sharePreview()}
                      type={TwButtonType.primary}
                      disabled={!selectedDriver || !radio}
                    />
                  </Stack>
                </div>
              </Box>
            </Box>
          </>
        )}

        {!isNormalShare && (
          <Stack
            className="notify-driver__container"
            spacing={1}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            width="100%"
          >
            <TwButton label="Skip" action={handleClose} type={TwButtonType.secondary} />
            <TwButton
              label="Share"
              action={sendUpdate}
              type={TwButtonType.primary}
              disabled={
                (assignedPayload?.length === 0 && unassignedPayload?.length === 0) || isSending
              }
            />
          </Stack>
        )}
      </TwDialog>
    </>
  );
};

TwNotifyDriver.defaultProps = {
  isSplitLoad: false
};

export default TwNotifyDriver;
